// apps/frontend/src/hooks/useChannelSession.ts

import { useCallback, useEffect, useRef, useState } from 'react';

import { configurationsApi } from '@/lib/api/configurations.api';
import { getEnv } from '@/lib/env';

export type ChannelConnectionState =
  | 'idle'
  | 'starting'
  | 'connecting'
  | 'connected'
  | 'ending'
  | 'ended'
  | 'error';

export interface TranscriptEntry {
  type: 'transcript';
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  replay: boolean;
}

export interface ToolExecutedEntry {
  type: 'tool_executed';
  id: string;
  toolName: string;
  toolResult?: unknown;
  timestamp: string;
}

export type TimelineEntry = TranscriptEntry | ToolExecutedEntry;

// ── Server message types ────────────────────────────────────────────

interface ReadyMessage {
  type: 'ready';
  callId: string;
  callSid: string;
}

interface CallMaterializedMessage {
  type: 'call_materialized';
  callId: string;
  callSid: string;
}

interface TranscriptMessage {
  type: 'transcript';
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  replay?: boolean;
}

interface ToolExecutedMessage {
  type: 'tool_executed';
  toolName: string;
  toolResult?: unknown;
  timestamp: string;
}

interface ErrorMessage {
  type: 'error';
  error: string;
}

interface EndedMessage {
  type: 'ended';
  callId: string;
  callSid: string;
  reason: string;
  endedAt: string;
}

interface AnswerMessage {
  type: 'webrtc_answer';
  sdp: string;
}

interface IceCandidateMessage {
  type: 'webrtc_ice_candidate';
  candidate: RTCIceCandidateInit | null;
}

interface IceServersMessage {
  type: 'webrtc_ice_servers';
  servers: RTCIceServer[];
}

interface WebRtcStateMessage {
  type: 'webrtc_state';
  enabled: boolean;
  status: 'idle' | 'negotiating' | 'connected' | 'unsupported' | 'error';
  reason?: string;
}

interface PongMessage {
  type: 'pong';
}

type ServerMessage =
  | ReadyMessage
  | CallMaterializedMessage
  | TranscriptMessage
  | ToolExecutedMessage
  | ErrorMessage
  | EndedMessage
  | AnswerMessage
  | IceCandidateMessage
  | IceServersMessage
  | WebRtcStateMessage
  | PongMessage;

// ── Hook ────────────────────────────────────────────────────────────

export interface UseChannelSessionResult {
  connectionState: ChannelConnectionState;
  timeline: TimelineEntry[];
  error: string | null;
  callId: string | null;
  callSid: string | null;
  isActive: boolean;
  start: (configId: string) => Promise<void>;
  stop: () => Promise<void>;
  clearTimeline: () => void;
}

const DEFAULT_ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }];
const ICE_SIGNAL_TIMEOUT_MS = 2500;
const END_SIGNAL_GRACE_MS = 200;

function buildEntryKey(type: string, content: string, timestamp: string): string {
  return `${type}|${timestamp}|${content}`;
}

export function useChannelSession(): UseChannelSessionResult {
  const [connectionState, setConnectionState] = useState<ChannelConnectionState>('idle');
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [callId, setCallId] = useState<string | null>(null);
  const [callSid, setCallSid] = useState<string | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const peerRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const remoteAudioRef = useRef<HTMLAudioElement | null>(null);
  const seenEntriesRef = useRef<Set<string>>(new Set());
  const isStoppingRef = useRef(false);
  const iceServersRef = useRef<RTCIceServer[]>(DEFAULT_ICE_SERVERS);
  const iceServersResolverRef = useRef<(() => void) | null>(null);

  const clearMedia = useCallback(() => {
    const peer = peerRef.current;
    peerRef.current = null;
    if (peer) {
      peer.onicecandidate = null;
      peer.onconnectionstatechange = null;
      peer.ontrack = null;
      peer.close();
    }

    const localStream = localStreamRef.current;
    localStreamRef.current = null;
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
    }

    const remoteStream = remoteStreamRef.current;
    remoteStreamRef.current = null;
    if (remoteStream) {
      remoteStream.getTracks().forEach((track) => track.stop());
    }

    const remoteAudio = remoteAudioRef.current;
    remoteAudioRef.current = null;
    if (remoteAudio) {
      remoteAudio.srcObject = null;
    }
  }, []);

  const closeSocket = useCallback(() => {
    const ws = wsRef.current;
    wsRef.current = null;
    if (!ws) return;
    if (ws.readyState === WebSocket.OPEN) {
      ws.close(1000, 'client_close');
      return;
    }
    ws.close();
  }, []);

  const waitForIceServersSignal = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        iceServersResolverRef.current = null;
        resolve();
      }, ICE_SIGNAL_TIMEOUT_MS);

      iceServersResolverRef.current = () => {
        clearTimeout(timeout);
        iceServersResolverRef.current = null;
        resolve();
      };
    });
  }, []);

  const appendEntry = useCallback((entry: TimelineEntry) => {
    const key =
      entry.type === 'transcript'
        ? buildEntryKey('t', entry.content, entry.timestamp)
        : buildEntryKey('tool', entry.toolName, entry.timestamp);

    if (seenEntriesRef.current.has(key)) return;
    seenEntriesRef.current.add(key);
    setTimeline((prev) => [...prev, { ...entry, id: key }]);
  }, []);

  const startPeerNegotiation = useCallback(async () => {
    const ws = wsRef.current;
    if (ws?.readyState !== WebSocket.OPEN) return;

    if (typeof window === 'undefined' || typeof RTCPeerConnection === 'undefined') {
      setConnectionState('error');
      setError('WebRTC is not supported in this browser.');
      return;
    }

    try {
      await waitForIceServersSignal();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const peer = new RTCPeerConnection({
        iceServers: iceServersRef.current,
        bundlePolicy: 'max-bundle',
      });

      const remoteStream = new MediaStream();
      const remoteAudio = new Audio();
      remoteAudio.autoplay = true;
      remoteAudio.srcObject = remoteStream;

      peerRef.current = peer;
      localStreamRef.current = stream;
      remoteStreamRef.current = remoteStream;
      remoteAudioRef.current = remoteAudio;

      stream.getTracks().forEach((track) => {
        peer.addTrack(track, stream);
      });

      peer.onicecandidate = (event) => {
        const socket = wsRef.current;
        if (socket?.readyState !== WebSocket.OPEN) return;
        socket.send(
          JSON.stringify({
            type: 'webrtc_ice_candidate',
            candidate: event.candidate ? event.candidate.toJSON() : null,
          }),
        );
      };

      peer.onconnectionstatechange = () => {
        if (peer.connectionState === 'connected') {
          setConnectionState('connected');
          setError(null);
          return;
        }
        if (
          peer.connectionState === 'failed' ||
          peer.connectionState === 'disconnected' ||
          peer.connectionState === 'closed'
        ) {
          setConnectionState('error');
          setError(`WebRTC connection ${peer.connectionState}.`);
        }
      };

      peer.ontrack = (event) => {
        if (event.track.kind !== 'audio') return;
        const target = remoteStreamRef.current;
        if (!target) return;
        target.addTrack(event.track);

        const audio = remoteAudioRef.current;
        if (audio) {
          void audio.play().catch(() => {
            // Some browsers may require a second user interaction before playback.
          });
        }
      };

      const offer = await peer.createOffer({ offerToReceiveAudio: true });
      await peer.setLocalDescription(offer);

      ws.send(
        JSON.stringify({
          type: 'webrtc_offer',
          sdp: offer.sdp ?? '',
        }),
      );
    } catch (err) {
      clearMedia();
      setConnectionState('error');
      setError(err instanceof Error ? err.message : 'Failed to start WebRTC negotiation.');
    }
  }, [clearMedia, waitForIceServersSignal]);

  const start = useCallback(
    async (configId: string) => {
      if (!configId || connectionState === 'starting' || connectionState === 'connecting') return;

      isStoppingRef.current = false;
      seenEntriesRef.current.clear();
      setTimeline([]);
      setCallId(null);
      setCallSid(null);
      iceServersRef.current = DEFAULT_ICE_SERVERS;
      setError(null);
      setConnectionState('starting');

      try {
        // The response is a stateless ticket; the session (and its callSid /
        // callId) is created by the gateway when the WebSocket connects.
        const startResult = await configurationsApi.testConfigurationWebcall(configId);

        const apiBase = getEnv('VITE_API_URL');
        const apiOrigin = new URL(apiBase);
        const wsProtocol = apiOrigin.protocol === 'https:' ? 'wss:' : 'ws:';
        const wsUrl = `${wsProtocol}//${apiOrigin.host}${startResult.wsPath}/${encodeURIComponent(startResult.wsToken)}`;

        const ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        setConnectionState('connecting');

        ws.onmessage = (event: MessageEvent<string>) => {
          let message: ServerMessage;
          try {
            message = JSON.parse(event.data) as ServerMessage;
          } catch {
            return;
          }

          switch (message.type) {
            case 'ready':
              setCallSid(message.callSid || null);
              setCallId(message.callId || null);
              void startPeerNegotiation();
              return;
            case 'call_materialized':
              setCallId(message.callId || null);
              return;
            case 'transcript':
              appendEntry({
                type: 'transcript',
                id: '',
                role: message.role,
                content: message.content,
                timestamp: message.timestamp,
                replay: message.replay ?? false,
              });
              return;
            case 'tool_executed':
              appendEntry({
                type: 'tool_executed',
                id: '',
                toolName: message.toolName,
                toolResult: message.toolResult,
                timestamp: message.timestamp,
              });
              return;
            case 'webrtc_ice_servers':
              iceServersRef.current = message.servers.length
                ? message.servers
                : DEFAULT_ICE_SERVERS;
              iceServersResolverRef.current?.();
              return;
            case 'webrtc_answer':
              if (peerRef.current && message.sdp) {
                void peerRef.current.setRemoteDescription({ type: 'answer', sdp: message.sdp });
              }
              return;
            case 'webrtc_ice_candidate':
              if (peerRef.current && message.candidate) {
                void peerRef.current.addIceCandidate(message.candidate);
              }
              return;
            case 'webrtc_state':
              if (
                !message.enabled ||
                message.status === 'unsupported' ||
                message.status === 'error'
              ) {
                setConnectionState('error');
                setError(message.reason || 'WebRTC backend is unavailable.');
                isStoppingRef.current = true;
                closeSocket();
              }
              if (message.status === 'connected') {
                setConnectionState('connected');
                setError(null);
              }
              return;
            case 'ended':
              setConnectionState('ended');
              setError(null);
              clearMedia();
              closeSocket();
              return;
            case 'error':
              setConnectionState('error');
              setError(message.error);
              return;
            case 'pong':
              return;
            default:
              return;
          }
        };

        ws.onerror = () => {
          setConnectionState('error');
          setError('WebSocket connection error.');
          isStoppingRef.current = true;
          closeSocket();
        };

        ws.onclose = (event) => {
          iceServersResolverRef.current?.();
          if (isStoppingRef.current) return;
          clearMedia();
          // Application close codes (4xxx) mean the server rejected the
          // connection (invalid/expired/replayed ticket, missing config, …).
          if (event.code >= 4000) {
            setConnectionState('error');
            setError(
              event.reason
                ? `Connection rejected: ${event.reason} (${event.code})`
                : `Connection rejected (code ${event.code})`,
            );
            return;
          }
          setConnectionState((prev) => (prev === 'ended' ? prev : 'ended'));
        };
      } catch (err) {
        setConnectionState('error');
        setError(err instanceof Error ? err.message : 'Failed to start channel session.');
      }
    },
    [appendEntry, clearMedia, closeSocket, connectionState, startPeerNegotiation],
  );

  const stop = useCallback(async () => {
    isStoppingRef.current = true;
    setConnectionState('ending');

    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'end' }));
      await new Promise((resolve) => setTimeout(resolve, END_SIGNAL_GRACE_MS));
    }

    iceServersResolverRef.current?.();
    clearMedia();
    closeSocket();
    setConnectionState('ended');
  }, [clearMedia, closeSocket]);

  const clearTimeline = useCallback(() => {
    seenEntriesRef.current.clear();
    setTimeline([]);
  }, []);

  useEffect(() => {
    return () => {
      isStoppingRef.current = true;
      iceServersResolverRef.current?.();
      clearMedia();
      closeSocket();
    };
  }, [clearMedia, closeSocket]);

  return {
    connectionState,
    timeline,
    error,
    callId,
    callSid,
    isActive: ['starting', 'connecting', 'connected', 'ending'].includes(connectionState),
    start,
    stop,
    clearTimeline,
  };
}
